import axios from 'axios';

// Short-lived, bounded cache shared by navigation and link prefetches.
const cache = new Map();
const pending = new Map();
const TTL = 60 * 1000;
const POST_PERSIST_TTL = 30 * 60 * 1000; // 30 minutes in storage for posts
const STORAGE_PREFIX = 'asa_bcache:';
const POST_STORAGE_PREFIX = 'asa_post:';

const keyFor = (path, params) => JSON.stringify([path, params || {}]);

function getSessionItem(key) {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (entry && entry.expires > Date.now()) return entry.data;
    window.sessionStorage.removeItem(STORAGE_PREFIX + key);
  } catch (e) {}
  return null;
}

function setSessionItem(key, data, ttl) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({
      data,
      expires: Date.now() + ttl
    }));
  } catch (e) {}
}

export function getFastBlogData(slug) {
  if (!slug) return null;
  if (typeof document !== 'undefined') {
    try {
      const initial = JSON.parse(document.getElementById('blog-initial-data')?.textContent || 'null');
      if (initial?.slug === slug) return initial;
    } catch { /* Ignore malformed bootstrap data and use the API. */ }
  }
  const path = blogPostPath(slug);
  const mem = cache.get(keyFor(path));
  if (mem && mem.data) return mem.data;

  if (typeof window !== 'undefined') {
    try {
      const storage = window.sessionStorage || window.localStorage;
      if (storage) {
        const raw = storage.getItem(POST_STORAGE_PREFIX + slug);
        if (raw) {
          const entry = JSON.parse(raw);
          if (entry && entry.data && (Date.now() - entry.savedAt < POST_PERSIST_TTL)) {
            cache.set(keyFor(path), { data: entry.data, expires: Date.now() + TTL });
            return entry.data;
          }
        }
      }
    } catch (e) {}
  }
  return null;
}

export function saveFastBlogData(slug, data) {
  if (!slug || !data) return;
  if (typeof window !== 'undefined') {
    try {
      const storage = window.sessionStorage || window.localStorage;
      if (storage) {
        storage.setItem(POST_STORAGE_PREFIX + slug, JSON.stringify({
          data,
          savedAt: Date.now()
        }));
      }
    } catch (e) {}
  }
}

export function getCachedBlogData(path, params) {
  const key = keyFor(path, params);
  const entry = cache.get(key);
  if (entry && entry.expires > Date.now()) return entry.data;
  cache.delete(key);

  const stored = getSessionItem(key);
  if (stored !== null) {
    cache.set(key, { data: stored, expires: Date.now() + TTL });
    return stored;
  }
  return undefined;
}

export function fetchBlogData(path, params) {
  const cached = getCachedBlogData(path, params);
  if (cached !== undefined) return Promise.resolve(cached);
  const key = keyFor(path, params);
  if (pending.has(key)) return pending.get(key);
  const request = axios.get(path, { params: params || {}, timeout: 15000 }).then(({ data }) => {
    if (cache.size >= 40) cache.delete(cache.keys().next().value);
    cache.set(key, { data, expires: Date.now() + TTL });
    setSessionItem(key, data, TTL);

    if (path.startsWith('/public/blogs/')) {
      const rawSlug = path.replace('/public/blogs/', '');
      const decodedSlug = decodeURIComponent(rawSlug);
      saveFastBlogData(decodedSlug, data);
      if (data && data.slug && data.slug !== decodedSlug) {
        saveFastBlogData(data.slug, data);
      }
    }
    return data;
  }).finally(() => pending.delete(key));
  pending.set(key, request);
  return request;
}

export const blogPostPath = slug => `/public/blogs/${encodeURIComponent(slug)}`;
export function prefetchBlogPost(slug) {
  if (slug) fetchBlogData(blogPostPath(slug)).catch(() => {});
}

export function clearBlogCache() {
  cache.clear();
  if (typeof window !== 'undefined') {
    try {
      [window.sessionStorage, window.localStorage].forEach(storage => {
        if (!storage) return;
        const keysToRemove = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k && (k.startsWith(STORAGE_PREFIX) || k.startsWith(POST_STORAGE_PREFIX))) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach(k => storage.removeItem(k));
      });
    } catch (e) {}
  }
}

